import Portal from "elements/Portal";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  getFlyCloneStyle,
  IFlyToCartProductVisual,
  IFlyToCartRects,
  runFlyToCartAnimation,
} from "./flyToCart";
import "./flyToCart.scss";

interface IFlyToCartItem {
  id: number;
  rects: IFlyToCartRects;
  product: IFlyToCartProductVisual;
}

export interface IFlyToCartTriggerPayload {
  rects: IFlyToCartRects;
  product: IFlyToCartProductVisual;
}

export interface IFlyToCartLayerHandle {
  trigger: (payload: IFlyToCartTriggerPayload) => void;
}

interface IFlyCloneProps {
  rects: IFlyToCartRects;
  product: IFlyToCartProductVisual;
  onComplete: () => void;
}

function FlyClone({ rects, product, onComplete }: IFlyCloneProps) {
  const cloneRef = useRef<HTMLDivElement>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const element = cloneRef.current;
    if (!element) {
      return;
    }

    let cancelled = false;
    let stopAnimation: (() => void) | undefined;
    let outerFrameId = 0;
    let innerFrameId = 0;

    outerFrameId = requestAnimationFrame(() => {
      innerFrameId = requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }
        stopAnimation = runFlyToCartAnimation(element, rects, () => {
          if (!cancelled) {
            onCompleteRef.current();
          }
        });
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(outerFrameId);
      cancelAnimationFrame(innerFrameId);
      stopAnimation?.();
    };
  }, [rects]);

  return (
    <div
      ref={cloneRef}
      className="fly-to-cart-clone"
      style={getFlyCloneStyle(rects.sourceRect, product)}
      aria-hidden="true"
    >
      <div className="fly-to-cart-clone__title" style={product.titleStyle}>
        {product.title}
      </div>
    </div>
  );
}

const FlyToCartLayer = forwardRef<IFlyToCartLayerHandle>((_, ref) => {
  const [flies, setFlies] = useState<IFlyToCartItem[]>([]);
  const flyIdRef = useRef(0);

  useImperativeHandle(ref, () => ({
    trigger: (payload: IFlyToCartTriggerPayload) => {
      flyIdRef.current += 1;
      const id = flyIdRef.current;
      setFlies(prev => [...prev, { id, rects: payload.rects, product: payload.product }]);
    },
  }));

  const handleComplete = (id: number) => {
    setFlies(prev => prev.filter(fly => fly.id !== id));
  };

  if (flies.length === 0) {
    return null;
  }

  return (
    <Portal>
      {flies.map(fly => (
        <FlyClone
          key={fly.id}
          rects={fly.rects}
          product={fly.product}
          onComplete={() => handleComplete(fly.id)}
        />
      ))}
    </Portal>
  );
});

FlyToCartLayer.displayName = "FlyToCartLayer";

export default FlyToCartLayer;
